import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

/**
 * Kontextuelle Hilfe-Tour fuer die Seite "Feiertage" (`/settings/holidays`).
 * Wird ueber den `?`-Button im Settings-Sticky-Header gestartet.
 *
 * Fuenf Schritte:
 *  1. Welcome + Jahres-Selektor
 *  2. Feiertags-Grid (Auswahl per Klick)
 *  3. Work-Mode-Tri-Toggle (Default/Geoeffnet/Geschlossen) — Spotlight auf
 *     erste ausgewaehlte Karte, daher `skipIf`, falls keiner aktiv ist.
 *  4. Eigenen Feiertag hinzufuegen
 *  5. Zusammenfassung
 *
 * Tour-Texte in `de.json/en.json` unter `TUTORIAL.SETTINGS_HOLIDAYS.*`,
 * Voice-Over-Manifest unter `settings-holidays-help`.
 */
export const SETTINGS_HOLIDAYS_HELP_TOUR: TourConfig = {
  id: TourFeature.SettingsHolidaysHelp,
  version: 1,
  title: 'SETTINGS_HOLIDAYS_HELP_TOUR_TITLE',
  description: 'SETTINGS_HOLIDAYS_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  triggerContext: { route: '/settings/holidays' },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 90,
  category: 'feature-discovery',
  chapters: [
    {
      id: 'overview',
      title: 'SETTINGS_HOLIDAYS_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'welcome',
          // Anker (Popover-Position) am Jahres-Selektor, weil er der visuell
          // "kompakteste" Teil oben ist — der Popover sitzt damit gut lesbar
          // direkt darunter. Der Spotlight rahmt aber den GANZEN Bereich ein
          // (Wrapper `holiday-page`), damit der User auf einen Blick sieht,
          // was alles zu dieser Seite gehoert: Jahres-Selektor, Uebersicht,
          // Feiertags-Grid, Plus-Karte fuer eigene Feiertage.
          anchor: AnchorId.HolidayYearSelector,
          spotlightAnchor: AnchorId.HolidayPage,
          title: 'TUTORIAL.SETTINGS_HOLIDAYS.WELCOME.TITLE',
          content: 'TUTORIAL.SETTINGS_HOLIDAYS.WELCOME.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'selection',
          anchor: AnchorId.HolidaySelectionGrid,
          spotlightAnchor: AnchorId.HolidaySelectionGrid,
          title: 'TUTORIAL.SETTINGS_HOLIDAYS.SELECTION.TITLE',
          content: 'TUTORIAL.SETTINGS_HOLIDAYS.SELECTION.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'work-mode',
          anchor: AnchorId.HolidayWorkModeFirst,
          spotlightAnchor: AnchorId.HolidayWorkModeFirst,
          title: 'TUTORIAL.SETTINGS_HOLIDAYS.WORK_MODE.TITLE',
          content: 'TUTORIAL.SETTINGS_HOLIDAYS.WORK_MODE.CONTENT',
          placement: 'right',
          nextCondition: 'manual',
          showNextButton: true,
          // Step wird still uebersprungen, wenn kein Feiertag aktiv ist —
          // der Anker existiert dann nicht im DOM.
          skipIf: () =>
            !document.querySelector('[onboardingAnchor="holiday-work-mode-first"]'),
        },
        {
          id: 'add-custom',
          anchor: AnchorId.HolidayAddCustomCard,
          spotlightAnchor: AnchorId.HolidayAddCustomCard,
          title: 'TUTORIAL.SETTINGS_HOLIDAYS.ADD_CUSTOM.TITLE',
          content: 'TUTORIAL.SETTINGS_HOLIDAYS.ADD_CUSTOM.CONTENT',
          placement: 'top',
          nextCondition: 'manual',
          showNextButton: true,
        },
        {
          id: 'summary',
          anchor: AnchorId.HolidaySummary,
          spotlightAnchor: AnchorId.HolidaySummary,
          title: 'TUTORIAL.SETTINGS_HOLIDAYS.SUMMARY.TITLE',
          content: 'TUTORIAL.SETTINGS_HOLIDAYS.SUMMARY.CONTENT',
          placement: 'bottom',
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: true,
        },
      ],
    },
  ],
};
